// client/src/components/pages/Geofencing/VehicleRail.tsx (converted from .js, ST-00244)
// LexiStar live-map vehicle rail. Desktop: a static left column beside the map.
// Below lg: a collapsible top-sheet drawer toggled by a summary button (mining
// supervisors work off phones). Data is the class-filtered /fleet set owned by
// the Live View page (LexiStar/LiveView, ST-00210); selecting a row lifts the regNo
// up for map + detail sync.
//
// ST-00253 / ST-00244: each row's dot and pill, and the summary chips, take the vehicle's colour
// from the shared asset status scheme via `statusOf` (Live View passes the LexiStar status
// board's key when the vehicle has one, else its motion) — the same colour as its map marker,
// the Status Board and Replay.
import { useMemo, useState } from 'react';
import { FaSearch, FaTimes, FaChevronDown, FaTruck } from 'react-icons/fa';
import { ASSET_STATUS_CLASSES, ASSET_STATUS_ORDER } from '../../map/assetStatus';
import {
  liveStatusOf,
  StatusDot,
  StatusWithMarks,
  ClassBadge,
  compactAge,
  type LiveStatus,
} from './vehicleStatus';
import type { FleetVehicle } from '../../../types/clientPages/geofencing';

const defaultStatusOf = (v: FleetVehicle): LiveStatus => liveStatusOf(v.status);

// ─── Status summary chips ("2 travelling full · 1 offline") ─────────────────────────────────
function StatusSummary({
  statuses,
  className = '',
}: {
  statuses: LiveStatus[];
  className?: string;
}) {
  const chips = useMemo(() => {
    const byLabel = new Map<string, { status: LiveStatus; count: number }>();
    statuses.forEach((s) => {
      const id = `${s.key}|${s.label}`;
      const cur = byLabel.get(id);
      if (cur) cur.count += 1;
      else byLabel.set(id, { status: s, count: 1 });
    });
    return [...byLabel.values()].sort(
      (a, b) =>
        ASSET_STATUS_ORDER.indexOf(a.status.key) - ASSET_STATUS_ORDER.indexOf(b.status.key) ||
        a.status.label.localeCompare(b.status.label)
    );
  }, [statuses]);
  if (chips.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {chips.map(({ status, count }) => (
        <span
          key={`${status.key}|${status.label}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-[11px] font-medium text-slate-600"
          data-status-key={status.key}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${ASSET_STATUS_CLASSES[status.key].dot}`}
          />
          {count} {status.label.toLowerCase()}
        </span>
      ))}
    </div>
  );
}

// ─── A single vehicle row ───────────────────────────────────────────────────
interface RowProps {
  v: FleetVehicle;
  status: LiveStatus;
  selected: boolean;
  onSelect: (regNo: string) => void;
}

function VehicleRow({ v, status, selected, onSelect }: RowProps) {
  const isMoving = v.status === 'moving';
  return (
    <button
      type="button"
      onClick={() => onSelect(v.regNo)}
      aria-pressed={selected}
      className={`w-full text-left px-3 py-2.5 rounded-lg border flex items-start gap-2.5 transition-all ${
        selected
          ? 'border-teal-500 ring-1 ring-teal-500 bg-teal-50/60 shadow-sm'
          : 'border-slate-200 bg-surface hover:border-slate-300 hover:shadow-sm'
      } ${v.status === 'offline' ? 'opacity-70' : ''}`}
    >
      <StatusDot status={status} className="mt-1.5 w-2.5 h-2.5" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm text-slate-800 truncate">{v.regNo}</span>
          <ClassBadge vehicleClass={v.vehicleClass} />
        </div>
        {v.label && <p className="text-xs text-slate-500 truncate">{v.label}</p>}
        {/* What it is doing, in its colour (the same as its marker, the Status Board and Replay). */}
        <div className="mt-1">
          {/* ST-00276: and the organisation's marks for it (fuel loss, overspeed …). */}
          <StatusWithMarks status={status} />
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-400">
          <span className="tabular-nums">{v.todayKm ?? 0} km today</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">{compactAge(v.ts, v.ageSeconds)}</span>
        </div>
      </div>

      {isMoving && (
        <span className="flex-shrink-0 text-sm font-bold text-slate-800 tabular-nums whitespace-nowrap">
          {Math.round(v.speed || 0)}
          <span className="text-[10px] font-normal text-slate-400"> km/h</span>
        </span>
      )}
    </button>
  );
}

// ─── Rail body (shared by the desktop column and the mobile drawer) ─────────
interface BodyProps {
  vehicles: FleetVehicle[];
  loading: boolean;
  selectedRegNo: string | null;
  onSelect: (regNo: string) => void;
  statusOf: (v: FleetVehicle) => LiveStatus;
}

function RailBody({ vehicles, loading, selectedRegNo, onSelect, statusOf }: BodyProps) {
  const [search, setSearch] = useState('');

  const statuses = useMemo(() => vehicles.map(statusOf), [vehicles, statusOf]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter(
      (v) => (v.regNo || '').toLowerCase().includes(q) || (v.label || '').toLowerCase().includes(q)
    );
  }, [vehicles, search]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search */}
      <div className="relative mb-2 flex-shrink-0">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reg or name…"
          className="w-full pl-8 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-surface text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
            aria-label="Clear search"
          >
            <FaTimes size={11} />
          </button>
        )}
      </div>

      {/* Status summary */}
      {!loading && vehicles.length > 0 && (
        <StatusSummary statuses={statuses} className="mb-2 flex-shrink-0" />
      )}

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-0.5">
        {loading && vehicles.length === 0 ? (
          // Loading skeleton
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-slate-200 bg-surface px-3 py-2.5 flex items-start gap-2.5 animate-pulse"
            >
              <span className="mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-200 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 rounded bg-slate-200" />
                <div className="h-2.5 w-16 rounded bg-slate-100" />
              </div>
            </div>
          ))
        ) : vehicles.length === 0 ? (
          // Empty state
          <div className="rounded-lg border border-dashed border-slate-300 bg-surface px-4 py-10 text-center">
            <FaTruck className="mx-auto text-slate-300 mb-3" size={30} />
            <p className="text-sm font-medium text-slate-600">No vehicles reporting yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Register a device in Settings › LexiStar Devices
            </p>
          </div>
        ) : filtered.length === 0 ? (
          // No search matches
          <div className="rounded-lg border border-dashed border-slate-300 bg-surface px-4 py-8 text-center">
            <p className="text-sm text-slate-500">No vehicles match “{search}”.</p>
          </div>
        ) : (
          filtered.map((v) => (
            <VehicleRow
              key={v.regNo}
              v={v}
              status={statusOf(v)}
              selected={v.regNo === selectedRegNo}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export interface VehicleRailProps {
  vehicles?: FleetVehicle[];
  loading?: boolean;
  selectedRegNo: string | null;
  onSelect: (regNo: string) => void;
  /** The vehicle's status (colour key + words); default: its motion only. */
  statusOf?: (v: FleetVehicle) => LiveStatus;
}

export default function VehicleRail({
  vehicles = [],
  loading = false,
  selectedRegNo,
  onSelect,
  statusOf = defaultStatusOf,
}: VehicleRailProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Selecting from the mobile drawer should close it so the map is visible.
  const handleSelect = (regNo: string) => {
    onSelect(regNo);
    setDrawerOpen(false);
  };

  const movingCount = vehicles.filter((v) => v.status === 'moving').length;

  return (
    <>
      {/* ── Desktop: static left column (stretches to the map's height) ── */}
      <aside className="hidden lg:flex flex-col w-80 flex-shrink-0 self-stretch min-h-0 bg-slate-50/60 rounded-lg border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Vehicles ({vehicles.length})
          </h3>
          {movingCount > 0 && (
            <span className="text-[11px] font-medium text-slate-600">
              {movingCount} on the move
            </span>
          )}
        </div>
        <RailBody
          vehicles={vehicles}
          loading={loading}
          selectedRegNo={selectedRegNo}
          onSelect={onSelect}
          statusOf={statusOf}
        />
      </aside>

      {/* ── Mobile: summary button that opens a top-sheet drawer ── */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="w-full flex items-center justify-between gap-2 bg-surface rounded-lg border border-slate-200 px-3 py-2.5 shadow-sm"
        >
          <span className="flex items-center gap-2 min-w-0">
            <FaTruck className="text-teal-500 flex-shrink-0" size={14} />
            <span className="text-sm font-semibold text-slate-800">
              Vehicles ({vehicles.length})
            </span>
          </span>
          <span className="flex items-center gap-2 min-w-0">
            {selectedRegNo ? (
              <span className="text-xs text-slate-500 truncate max-w-[120px]">{selectedRegNo}</span>
            ) : (
              movingCount > 0 && (
                <span className="text-[11px] font-medium text-slate-600">{movingCount} moving</span>
              )
            )}
            <FaChevronDown className="text-slate-400 flex-shrink-0" size={12} />
          </span>
        </button>

        {drawerOpen && (
          <div
            className="fixed inset-0 z-50 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Vehicle list"
          >
            <div
              className="absolute inset-0 bg-slate-900/40 dark:bg-black/40 animate-fadeIn"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-x-0 top-0 bg-surface rounded-b-2xl shadow-xl max-h-[85vh] flex flex-col p-3 pt-4 animate-slideIn">
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <h3 className="text-sm font-bold text-slate-800">Vehicles ({vehicles.length})</h3>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="p-1.5 -mr-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
                  aria-label="Close vehicle list"
                >
                  <FaTimes size={16} />
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <RailBody
                  vehicles={vehicles}
                  loading={loading}
                  selectedRegNo={selectedRegNo}
                  onSelect={handleSelect}
                  statusOf={statusOf}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
